// src/components/SimpleComponents/SimpleUsers.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form, Select, Typography } from 'antd';
import { useSimpleFormContext } from './SimpleFormProvider';
import SimpleFormItem from './SimpleFormItem';
import SimpleAvatar from './SimpleAvatar';
import useFormElement from 'hooks/useFormElement';
import useZustand from 'hooks/useZustand';
import translateX from 'utils/translateX';

const { Text } = Typography;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SimpleUsers = ({
  name,
  subdocumentField,
  style = {},
  styleItem = {},
  disabled = false,
  required: requiredProp,
  onChange,
  ...props
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { locked, disabledFields, shouldShowErrors, requiredFields, form } =
    useSimpleFormContext();
  const storedTeam = useZustand('auth', 'team');
  const storedMe = useZustand('auth', 'me');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const elementProps = useFormElement(name, subdocumentField);
  const { elementRef, fullFieldName, flattenFieldName } = elementProps;
  const lastValueRef = useRef(null);
  const fieldValue = Form.useWatch(fullFieldName, form);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isDisabled = disabled || locked || disabledFields?.includes(flattenFieldName);
  const required = requiredProp ?? requiredFields?.includes(flattenFieldName);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  // ANTD v6 FIX: controlled open state prevents dropdown not closing on first click
  const [open, setOpen] = useState(false);
  // Hide avatar while user is typing a search query
  const [isSearching, setIsSearching] = useState(false);
  const [shouldValidate, setShouldValidate] = useState(false);
  const [customValidationStatus, setCustomValidationStatus] = useState('');
  const [customValidationMessage, setCustomValidationMessage] = useState('');

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (shouldShowErrors) setShouldValidate(true);
  }, [shouldShowErrors]);

  useEffect(() => {
    if (!shouldValidate) return;

    const isEmpty =
      fieldValue === undefined || fieldValue === null || fieldValue === '';

    if (isEmpty && required) {
      setCustomValidationStatus('error');
      setCustomValidationMessage(translateX('required_field'));
      return;
    }

    if (!isEmpty) {
      setCustomValidationStatus('success');
      setCustomValidationMessage('');
      return;
    }

    setCustomValidationStatus('');
    setCustomValidationMessage('');
  }, [fieldValue, required, shouldValidate]);

  // Pre-select the logged-in user when the field is empty
  useEffect(() => {
    const meId = storedMe?._id || storedMe?.accountId;
    if (!meId) return;
    const currentVal = form.getFieldValue(fullFieldName);
    if (currentVal) return;
    form.setFieldValue(fullFieldName, meId);
  }, [form, fullFieldName, storedMe]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  // ANTD v6 FIX: close dropdown explicitly on select
  const handleSelect = useCallback(() => {
    setOpen(false);
    setIsSearching(false);
  }, []);

  const handleDropdownVisibleChange = useCallback(visible => setOpen(visible), []);

  const handleSearch = useCallback(value => {
    setIsSearching(value.length > 0);
  }, []);

  const handleChange = useCallback(
    (value, option) => {
      if (value !== lastValueRef.current) {
        lastValueRef.current = value;
        if (typeof onChange === 'function') onChange(value, option);
      }
    },
    [onChange]
  );

  const handleClear = useCallback(() => {
    lastValueRef.current = null;
    setIsSearching(false);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const options = useMemo(() => buildTeamOptions(storedTeam), [storedTeam]);

  const selectStyle = useMemo(() => ({ height: 40, ...style }), [style]);

  const emptyContent = useMemo(
    () => (
      <div style={{ textAlign: 'center', padding: '8px 0' }}>
        <Text type="secondary">{translateX('no_data')}</Text>
      </div>
    ),
    []
  );

  // Show avatar + name for the selected value (ANTD v6 labelRender)
  // Returns null while searching so the avatar doesn't overlap the search input
  const labelRender = useCallback(
    ({ value }) => {
      if (!value || isSearching) return null;
      const option = options.find(o => o.value === value);
      if (!option) return null;
      const { avatarSrc, backColor, foreColor, label } = option;
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <SimpleAvatar
            size={22}
            src={avatarSrc}
            name={label}
            backColor={backColor}
            foreColor={foreColor}
            showBorder={false}
            showShadow={false}
          />
          <span>{label}</span>
        </div>
      );
    },
    [options, isSearching]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <SimpleFormItem
      {...props}
      name={fullFieldName}
      elementProps={elementProps}
      style={styleItem}
      required={required}
      customValidationStatus={customValidationStatus}
      customValidationMessage={customValidationMessage}
    >
      <Select
        ref={elementRef}
        id={flattenFieldName}
        style={selectStyle}
        options={options}
        showSearch
        allowClear
        filterOption={filterTeamOption}
        optionFilterProp="label"
        labelRender={labelRender}
        notFoundContent={emptyContent}
        disabled={isDisabled}
        open={open}
        onSelect={handleSelect}
        onOpenChange={handleDropdownVisibleChange}
        onSearch={handleSearch}
        onChange={handleChange}
        onClear={handleClear}
        optionRender={renderTeamOption}
      />
    </SimpleFormItem>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SimpleUsers;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function getAvatarSrc(member) {
  const avatar = member?.avatar;
  return avatar?.base64Thumb || avatar?.thumbUrl || avatar?.storageUrl || member?.img || null;
}

function buildTeamOptions(team = []) {
  return team
    .filter(m => m.status === 2)
    .map(m => ({
      value: m._id || m.accountId,
      label: m.name || m.fullName || '',
      text: m.name || m.fullName || '',
      avatarSrc: getAvatarSrc(m),
      backColor: m.backColor || '#e6f4ff',
      foreColor: m.foreColor || '#0958d9',
    }));
}

function filterTeamOption(input, option) {
  const label = option?.label ?? '';
  const labelStr = typeof label === 'string' ? label : '';
  return labelStr.toLowerCase().includes((input || '').toLowerCase());
}

function renderTeamOption(option) {
  const { avatarSrc, backColor, foreColor, label } = option.data;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <SimpleAvatar
        size={24}
        src={avatarSrc}
        name={label}
        backColor={backColor}
        foreColor={foreColor}
        showBorder={false}
        showShadow={false}
      />
      <span>{label}</span>
    </div>
  );
}
